import { Link } from "react-router-dom";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const anchors = [
  { id: "position", label: "网站定位" },
  { id: "mission", label: "我们的使命" },
  { id: "coverage", label: "内容范围" },
  { id: "team", label: "团队简介" },
  { id: "principle", label: "运营理念" },
  { id: "disclaimer", label: "免责声明" },
];

const teams = [
  { icon: "ri-search-eye-line", name: "调查组", desc: "负责线索核实与深度调查，还原事件全貌。" },
  { icon: "ri-government-line", name: "时政组", desc: "跟踪政策执行与公共事务动态，关注监督进展。" },
  { icon: "ri-line-chart-line", name: "财经组", desc: "聚焦市场与金融风险信号，解读数据背后的变化。" },
  { icon: "ri-flashlight-line", name: "突发组", desc: "第一时间奔赴现场，持续更新突发事件进展。" },
];

export default function AboutPage() {
  useDocumentTitle(
    "关于我们 - 911爆料",
    "了解 911爆料 的网站定位、使命、内容范围、团队构成与运营理念。"
  );

  return (
    <div className="animate-fade-up">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-shell px-4 py-14 md:px-6 md:py-16">
          <span className="font-label text-xs tracking-[0.3em] text-primary-700">
            ABOUT 911爆料
          </span>
          <h1 className="mt-3 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            让每一条爆料，都经得起追问
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            911爆料 是一个聚焦爆料新闻与深度事件分析的资讯平台，面向关注社会热点、追求真相与独家信息的读者，帮助大家在纷繁的信息流中快速掌握最新动态与内幕详情。
          </p>
        </div>
      </section>

      <div className="sticky top-16 z-30 border-b border-background-200 bg-background-100/95 backdrop-blur">
        <div className="mx-auto w-full max-w-shell overflow-x-auto px-4 md:px-6">
          <nav className="no-scrollbar flex items-center gap-2 py-3" aria-label="页面内导航">
            {anchors.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="whitespace-nowrap rounded-full border border-background-300 px-3.5 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-500 hover:text-foreground-950"
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      <div className="mx-auto w-full max-w-shell px-4 py-12 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <section id="position" className="scroll-mt-32 lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">网站定位</h2>
            <p className="mt-4 text-sm leading-8 text-foreground-700">
              911爆料 以「爆料 + 深度」为核心，既提供突发事件的即时更新，也提供对重点事件的系统梳理。我们相信，速度与严谨并不矛盾：快，是为了不被错过；稳，是为了不被误读。
            </p>
            <p className="mt-4 text-sm leading-8 text-foreground-700">
              平台围绕社会、政治、经济、突发、独家五大板块组织内容，并通过专题把分散的报道串联成完整的时间线与证据链。
            </p>
          </section>

          <aside className="rounded-lg border border-background-200 bg-background-100 p-6">
            <p className="font-heading text-4xl font-black text-accent-400">7×24</p>
            <p className="mt-2 text-sm text-foreground-600">
              小时值守，突发事件不间断跟进。
            </p>
            <div className="mt-5 h-px bg-background-300" />
            <p className="mt-5 font-heading text-4xl font-black text-primary-700">100%</p>
            <p className="mt-2 text-sm text-foreground-600">
              演示内容，全部为虚构示例数据。
            </p>
          </aside>
        </div>

        <section id="mission" className="mt-14 scroll-mt-32">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">我们的使命</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              { icon: "ri-time-line", title: "更快一步", desc: "突发事件第一时间呈现，持续滚动更新进展。" },
              { icon: "ri-file-search-line", title: "更深一层", desc: "以时间线与证据链，还原事件的前因后果。" },
              { icon: "ri-shield-check-line", title: "更实一点", desc: "多方交叉核实，谨慎对待每一条线索。" },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-800">
                  <i className={item.icon} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="coverage" className="mt-14 scroll-mt-32">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">内容范围</h2>
          <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              "社会民生与公共安全领域的热点事件追踪",
              "政策执行、监督问责与公共事务动态",
              "市场、金融与企业经营中的风险信号",
              "突发事件现场与最新进展的即时更新",
              "基于独家线索的深度调查与资料梳理",
              "重点事件的专题化追踪与背景还原",
            ].map((text) => (
              <li
                key={text}
                className="flex items-start gap-2.5 rounded-lg border border-background-200 bg-background-100 p-4 text-sm text-foreground-700"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-500 text-xs text-background-50">
                  <i className="ri-check-line" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </section>

        <section id="team" className="mt-14 scroll-mt-32">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">团队简介</h2>
          <p className="mt-3 text-sm text-foreground-600">
            为保护调查工作，团队成员均以小组形式署名。以下为示例介绍。
          </p>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {teams.map((member) => (
              <div
                key={member.name}
                className="rounded-lg border border-background-200 bg-background-100 p-5 text-center"
              >
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-primary-700">
                  <i className={member.icon} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
                  {member.name}
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  {member.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section id="principle" className="mt-14 scroll-mt-32">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">运营理念</h2>
          <div className="mt-5 space-y-4">
            {[
              { title: "以事实为底稿", desc: "对未核实的信息保持克制，对已核实的信息如实呈现。" },
              { title: "以读者为中心", desc: "用清晰的结构与摘要，降低读者获取信息的成本。" },
              { title: "以公开为准则", desc: "标注来源与时间，让每一条报道都可追溯、可对照。" },
            ].map((item) => (
              <div
                key={item.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <span className="mt-0.5 h-fit rounded-md bg-primary-500 px-2 py-1 text-xs font-bold text-foreground-950">
                  {item.title}
                </span>
                <p className="text-sm leading-relaxed text-foreground-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="disclaimer" className="mt-14 scroll-mt-32">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">免责声明</h2>
          <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-6">
            <p className="text-sm leading-8 text-foreground-700">
              本网站为演示作品，站内所有新闻标题、正文、来源、作者、联系方式及数据均为虚构示例，不代表任何真实事件、机构或个人。如与现实存在雷同，纯属巧合。
            </p>
            <p className="mt-4 text-sm leading-8 text-foreground-700">
              本站不构成任何形式的信息发布建议，请读者理性看待爆料内容，一切以权威部门发布为准。
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600"
            >
              提供爆料线索
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}